import { Trans, useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { formatCurrency } from "../../../utils";
import React, { useMemo } from "react";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { usePriceList } from "../../providers/PriceListProvider";
import { getOrdinal, upperCaseFirst } from "../../../lib/common";
import Popup, {
  PopupActions,
  PopupButton,
  PopupContent,
  PopupHeading,
} from "../../../components/Common/Popup";
import Container from "@material-ui/core/Container";
import { useShoppingCart } from "../../providers/ShoppingCartProvider";

const useStyles = makeStyles((theme) => ({
  priceCell: {
    //  textAlign: 'right'
  },
  oldPrice: {
    textDecoration: "line-through",
  },
  productTable: {
    "& > thead > tr > th": {
      backgroundColor: "#E4E4E4",
      textAlign: "center",
      fontWeight: "normal",
      padding: theme.spacing(1),
    },
    "& > tbody > tr > td": {
      textAlign: "center",
    },
  },
  dialogContent: {
    paddingLeft: 0,
    paddingRight: 0,
    "& h4": {
      textAlign: "center",
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(3),
    },
  },
  campaignText: {
    marginTop: 0,
    textAlign: "center",
  },
}));

export default ({ product, category, categories, open, handleClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { qualifyForPromotedPrice } = useShoppingCart();
  const { productHasCampaign, getPromotedPrice } = usePriceList();

  const hasCampaign = useMemo(() => {
    if (!product) {
      return false;
    }

    if (productHasCampaign(product, category)) {
      return true;
    }

    if (categories) {
      for (const category of categories) {
        if (productHasCampaign(product, category)) {
          return true;
        }
      }
    }
    return false;
  }, [product, category, categories]);

  let promotedPrice = 0;
  if (product && qualifyForPromotedPrice) {
    promotedPrice = getPromotedPrice(product);
  }

  let promotedPriceInfo = null;
  if (promotedPrice) {
    promotedPriceInfo = (
      <Container>
        <p
          dangerouslySetInnerHTML={{
            __html: t(
              "As you have a package in your shopping cart you qualify to buy this product at the discounted price of %s.",
              {
                postProcess: "sprintf",
                sprintf: ["<b>" + formatCurrency(promotedPrice) + "</b>"],
              },
            ),
          }}
        ></p>
        <p>
          <Trans>The regular prices are:</Trans>
        </p>
      </Container>
    );
  }

  if (!open) {
    return null;
  }

  let campaignPriceText = "";
  if (hasCampaign && product.campaignrequirement) {
    campaignPriceText = t("%s campaign!", {
      postProcess: "sprintf",
      sprintf: [upperCaseFirst(t(product.campaignrequirement).toLowerCase())],
    });
  } else if (hasCampaign) {
    campaignPriceText = t("Campaign!");
  }

  return (
    <Popup
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={open}
    >
      <PopupHeading id="simple-dialog-title">
        {t("Prices %s", {
          postProcess: "sprintf",
          sprintf: [product.name],
        })}
      </PopupHeading>
      <PopupContent className={classes.dialogContent}>
        {promotedPriceInfo}
        {campaignPriceText && (
          <p className={classes.campaignText}>{campaignPriceText}</p>
        )}
        <ProductPrices product={product} hasCampaign={hasCampaign} />
      </PopupContent>
      <PopupActions>
        <PopupButton onClick={handleClose} autoFocus>
          <Trans>Ok</Trans>
        </PopupButton>
      </PopupActions>
    </Popup>
  );
};

const ProductPrices = ({ hasCampaign, product }) => {
  if (product.subproducts.length == 0) {
    return (
      <ProductPrice
        product={product}
        subproduct={null}
        hasCampaign={hasCampaign}
      />
    );
  } else {
    return product.subproducts.map((subProduct) => {
      return (
        <ProductPrice
          product={product}
          subProduct={subProduct}
          hasCampaign={hasCampaign}
        />
      );
    });
  }
};

const ProductPrice = ({ product, hasCampaign, subProduct }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const prices = product.productprices.reduce((prices, price) => {
    if (prices.length > 0) {
      prices[prices.length - 1].quantityTo = price.quantity - 1;
    }

    let campaignPrice = 0;
    let subProductPrice = 0;
    if (subProduct) {
      subProductPrice += subProduct.extraprice;
    }
    if (hasCampaign) {
      campaignPrice = price.campaignprice;
    }

    console.log("The price is", price);
    prices.push({
      quantityFrom: price.quantity,
      quantityTo: 0,
      price: price.price + subProductPrice,
      campaignPrice: campaignPrice + subProductPrice,
      priceType: price.type,
    });
    return prices;
  }, []);

  console.log("Price type", prices);

  let productName = product.name;
  if (subProduct) {
    productName += " " + subProduct.name;
  }

  t("%dst");
  t("%dnd");
  t("%dr");
  t("%dth");
  const ordinals = {
    st: "%dst",
    nd: "%dnd",
    rd: "%drd",
    th: "%dth",
  };

  return (
    <>
      <h4>{productName}</h4>
      <Table className={classes.productTable}>
        <TableHead>
          <TableRow>
            <TableCell>
              <Trans>Quantity</Trans>
            </TableCell>
            <TableCell>
              <Trans>Price/pcs</Trans>
            </TableCell>
            {hasCampaign && (
              <TableCell>
                <Trans>Campaign price</Trans>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {prices.map((pp, index) => {
            console.log("Product price", pp);
            let qtyString;
            if (pp.priceType == 0) {
              qtyString = pp.quantityFrom;
              if (pp.quantityTo) {
                if (pp.quantityTo != pp.quantityFrom) {
                  qtyString += "-" + pp.quantityTo;
                }
              } else {
                qtyString += "+";
              }
              qtyString = t("%s pcs", {
                postProcess: "sprintf",
                sprintf: [qtyString],
              });
            } else {
              qtyString = t(ordinals[getOrdinal(pp.quantityFrom)], {
                postProcess: "sprintf",
                sprintf: [pp.quantityFrom],
              });
              if (pp.quantityTo) {
                if (pp.quantityTo != pp.quantityFrom) {
                  qtyString +=
                    "-" +
                    t(
                      ordinals[
                        (getOrdinal(pp.quantityTo),
                        {
                          postProcess: "sprintf",
                          sprintf: [pp.quantityTo],
                        })
                      ],
                    );
                }
              } else {
                qtyString += "+";
              }
            }
            return (
              <TableRow key={index}>
                <TableCell>{qtyString}</TableCell>
                <TableCell className={hasCampaign ? classes.oldPrice : ""}>
                  {formatCurrency(pp.price)}
                </TableCell>
                {hasCampaign && (
                  <TableCell>{formatCurrency(pp.campaignPrice)}</TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
};
